// «الجرد اليومي» — the day sheet's line model.
//
// A line on screen is an ENTRY of input strings:
//   { r | rp + rl,   received: one quantity, or whole packs + loose count units
//     s, to,         sent out to another branch (+ destination location id)
//     c | cp + cl,   closing: one quantity, or whole packs + loose
//     n }            note
// Received and sent are 0 when blank. Closing is NOT: blank is "not counted",
// and "0" is a counted zero that must stay counted (never `v && v > 0`).
//
// An EDITED entry also carries `base`: the stored line it was typed over (its
// inputs + its `at`). A save sends only the parts changed since `base`, with
// `base_at`; the server refuses a line stored since (DK_LINE_STALE), so a tab
// loaded before another device saved never blanks what it did not see.
import { isCounted } from "./dkUtils";

/** The item has a pack helper (carton / bucket): packs + loose inputs. */
export const isPackItem = (item) => !!item?.pack_unit && Number(item?.pack_size) > 0;

/** "5." -> "5", "." -> "", ".5" -> "0.5": what the server's quantity rule accepts. */
export function cleanQty(s) {
    let v = String(s ?? "").trim();
    if (v === ".") return "";
    if (v.endsWith(".")) v = v.slice(0, -1);
    if (v.startsWith(".")) v = `0${v}`;
    return v;
}

export const num = (s) => {
    const v = cleanQty(s);
    if (!isCounted(v)) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const round3 = (n) => Math.round(n * 1000) / 1000;
const str = (v) => (v === null || v === undefined ? "" : String(v));
// received / sent: a stored 0 shows as a blank input (blank IS 0 for these two)
const zeroBlank = (v) => (v === null || v === undefined || Number(v) === 0 ? "" : String(v));

/** A (restored) entry has the shape this item takes today: a draft saved before
 *  the pack helper was added or removed is dropped, never sent. */
export function entryFits(item, e) {
    if (!e || typeof e !== "object") return false;
    const s = (v) => typeof v === "string";
    const base = s(e.s) && s(e.to) && s(e.n);
    return isPackItem(item)
        ? base && s(e.rp) && s(e.rl) && s(e.cp) && s(e.cl)
        : base && s(e.r) && s(e.c);
}

/** The entry the SERVER's line implies (what the inputs show with no edit). */
export function serverEntry(item, line) {
    const ln = line || {};
    const out = { s: zeroBlank(ln.sent), to: str(ln.sent_to), n: str(ln.note) };
    if (!isPackItem(item)) {
        return { ...out, r: zeroBlank(ln.received), c: str(ln.closing) };
    }
    const hasRp = ln.received_packs !== null && ln.received_packs !== undefined;
    const rp = hasRp ? zeroBlank(ln.received_packs) : "";
    const rl = hasRp ? zeroBlank(ln.received_loose) : zeroBlank(ln.received);
    let cp = "";
    let cl = "";
    if (ln.closing !== null && ln.closing !== undefined) {
        if (ln.closing_packs !== null && ln.closing_packs !== undefined) {
            cp = String(ln.closing_packs);
            cl = str(ln.closing_loose);
        } else {
            // a closing saved as one quantity: all of it is loose
            cl = String(ln.closing);
        }
    }
    return { ...out, rp, rl, cp, cl };
}

/** Received in count units (blank = 0). */
export function receivedOf(item, e) {
    if (!e) return 0;
    if (!isPackItem(item)) return num(e.r) || 0;
    return round3((num(e.rp) || 0) * Number(item.pack_size) + (num(e.rl) || 0));
}

/** Sent in count units (blank = 0). */
export const sentOf = (e) => (e ? num(e.s) || 0 : 0);

/** Closing in count units, or null when not counted. */
export function closingOf(item, e) {
    if (!e) return null;
    if (!isPackItem(item)) return num(e.c);
    const p = num(e.cp);
    const l = num(e.cl);
    if (p === null && l === null) return null;
    return round3((p || 0) * Number(item.pack_size) + (l || 0));
}

const same = (a, b) => (num(a) || 0) === (num(b) || 0);

/** The inputs of each part of a line. A part is compared, sent and rebased as a
 *  whole: the packs and loose of one quantity always travel together. */
const PARTS = ["received", "sent", "closing", "note"];
const partKeys = (item, part) => {
    const pack = isPackItem(item);
    if (part === "received") return pack ? ["rp", "rl"] : ["r"];
    if (part === "closing") return pack ? ["cp", "cl"] : ["c"];
    return part === "sent" ? ["s", "to"] : ["n"];
};

/** The parts whose meaning differs between two entries. */
export function changedParts(item, a, b) {
    const out = [];
    const sentA = sentOf(a);
    // a destination only matters while something was sent
    if (sentA !== sentOf(b) || (sentA > 0 && (a.to || "") !== (b.to || ""))) out.push("sent");
    if ((a.n || "").trim() !== (b.n || "").trim()) out.push("note");
    if (isPackItem(item)) {
        if (!same(a.rp, b.rp) || !same(a.rl, b.rl)) out.push("received");
        const blankA = num(a.cp) === null && num(a.cl) === null;
        const blankB = num(b.cp) === null && num(b.cl) === null;
        if (blankA || blankB ? blankA !== blankB : !(same(a.cp, b.cp) && same(a.cl, b.cl))) out.push("closing");
    } else {
        if (!same(a.r, b.r)) out.push("received");
        if (num(a.c) !== num(b.c)) out.push("closing");
    }
    return out;
}

/** Two entries mean the same stored line. */
export function sameEntry(item, a, b) {
    if (!a || !b) return a === b;
    return !changedParts(item, a, b).length;
}

/** The stored line an edit is typed over: its inputs and its `at` (null: none stored). */
export const baseOf = (item, line) => ({ ...serverEntry(item, line), at: line?.at ?? null });

/** An entry moved onto a fresher stored line: the parts the user changed since
 *  its `base` over the fresh line's inputs, with the fresh line as its new base.
 *  An entry without a base (a draft from before bases) keeps every part that
 *  differs from the fresh line, as it always did. */
export function rebaseEntry(item, e, line) {
    const fresh = serverEntry(item, line);
    const out = { ...fresh, base: baseOf(item, line) };
    for (const part of changedParts(item, e, e.base || fresh)) {
        for (const k of partKeys(item, part)) out[k] = e[k];
    }
    return out;
}

/** The same input TEXT: "5." typed over a stored 5 is the same line but still
 *  being typed, so it stays on screen instead of snapping back. */
export function sameText(item, a, b) {
    if (!a || !b) return a === b;
    const keys = isPackItem(item) ? ["rp", "rl", "s", "to", "cp", "cl", "n"] : ["r", "s", "to", "c", "n"];
    return keys.every((k) => (a[k] ?? "") === (b[k] ?? ""));
}

/** One `PUT /dk/day` line: only the parts changed since `base` (the stored line
 *  the edit was typed over; the server keeps every field not sent), and
 *  `base_at`, its `at`, so the server refuses the line if it was stored again
 *  since. A sent blank received / sent = 0, a sent blank closing clears the count. */
export function toPayload(item, e, base) {
    const parts = base ? changedParts(item, e, base) : PARTS;
    const out = { item_id: item.id };
    if (base) out.base_at = base.at ?? null;
    if (parts.includes("received")) {
        if (isPackItem(item)) {
            out.received_packs = cleanQty(e.rp);
            out.received_loose = cleanQty(e.rl);
        } else {
            out.received = cleanQty(e.r);
        }
    }
    if (parts.includes("sent")) {
        out.sent = cleanQty(e.s);
        out.sent_to = num(out.sent) ? e.to || null : null;
    }
    if (parts.includes("closing")) {
        if (isPackItem(item)) {
            out.closing_packs = cleanQty(e.cp);
            out.closing_loose = cleanQty(e.cl);
        } else {
            out.closing = cleanQty(e.c);
        }
    }
    if (parts.includes("note")) out.note = (e.n || "").trim();
    return out;
}

/** The computed figures of a line: the server's for a saved line, a local
 *  preview (same formulas as SPEC §3.3) while it has unsaved edits. */
export function figuresOf(item, line, entry, dirty) {
    const ln = line || {};
    if (!dirty) {
        return {
            used: ln.used ?? null, theoretical: ln.theoretical ?? null, explained: ln.explained ?? null,
            variance: ln.variance ?? null, unexplained: ln.unexplained ?? null, flag: !!ln.flag, preview: false,
        };
    }
    const opening = ln.opening ?? null;
    const closing = closingOf(item, entry);
    const used = opening === null || closing === null
        ? null : round3(Number(opening) + receivedOf(item, entry) - sentOf(entry) - closing);
    const theoretical = ln.theoretical ?? null;
    const explained = ln.explained ?? null;
    const variance = used === null || theoretical === null ? null : round3(used - Number(theoretical));
    const unexplained = variance === null ? null : round3(variance - Number(explained || 0));
    return { used, theoretical, explained, variance, unexplained, flag: null, preview: true };
}
